import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { INITIAL_ROUTE, EXPERIENCE, PROJECTS, ARTICLES } from "../utils/routes";

const pages: Record<string, string> = {
  about: INITIAL_ROUTE,
  experience: EXPERIENCE,
  projects: PROJECTS,
  articles: ARTICLES,
};

const files = [
  "about.md",
  "experience.md",
  "projects.md",
  "articles.md",
  "resume.pdf",
];

const HELP: React.ReactNode = (
  <>
    {"available commands:\n"}
    {"  about · experience · projects · articles    open a page\n"}
    {"  skills            tech stack\n"}
    {"  contact           email · github · linkedin\n"}
    {"  resume            open resume.pdf\n"}
    {"  ls · cat <file>   look around\n"}
    {"  sudo hire ishak   try it\n"}
    {"  clear             clear the terminal"}
  </>
);

const CONTACT: React.ReactNode = (
  <>
    {"  mail      "}
    <a href="mailto:ertemishakk@gmail.com">ertemishakk@gmail.com</a>
    {"\n  github    "}
    <a
      href="https://github.com/ertemishakk"
      target="_blank"
      rel="noopener noreferrer"
    >
      github.com/ertemishakk
    </a>
    {"\n  linkedin  "}
    <a
      href="https://www.linkedin.com/in/ishak-ertem-054b5b139"
      target="_blank"
      rel="noopener noreferrer"
    >
      linkedin.com/in/ishak-ertem
    </a>
  </>
);

const SKILLS =
  "react · react native · typescript · next.js · node.js ·\nelectron · python · fastapi · mysql · nosql · websockets";

const CAT: Record<string, string> = {
  "about.md":
    "# Ishak Ertem — Senior Software Engineer\nShipping web, mobile, kiosk and desktop apps used by millions.\nFast learner: picked up Clarion (a legacy 4GL) in six months\nto migrate legacy systems to Python and React Native.",
  "experience.md":
    "Senior Software Engineer @ Redcat Cloud Solutions (2021—now)\nFull Stack Software Engineer @ Frozberries Vic (2019—2021)\nFreelance Software Engineer (since 2018)",
  "projects.md":
    "yakamoz — directory app with a community feed (React Native, Python, MongoDB)\ntiktok-pixel — npm package\nreact-formik-stepper — npm package",
  "articles.md": "run `articles` to read them on the site",
};

interface Line {
  id: number;
  input?: string;
  output?: React.ReactNode;
}

let nextId = 0;

const Terminal = () => {
  const navigate = useNavigate();
  const [lines, setLines] = useState<Line[]>([
    { id: nextId++, output: "welcome to ishak.sh — type `help` to get started" },
  ]);
  const [value, setValue] = useState("");
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [lines]);

  const run = (raw: string): React.ReactNode => {
    const input = raw.trim();
    const [cmd, ...args] = input.split(/\s+/);
    const arg = args.join(" ");

    switch (cmd) {
      case "":
        return undefined;
      case "help":
        return HELP;
      case "whoami":
        return "ishak ertem — senior software engineer";
      case "ls":
        return files.join("  ");
      case "pwd":
        return "~/portfolio";
      case "date":
        return new Date().toString();
      case "skills":
        return SKILLS;
      case "contact":
        return CONTACT;
      case "resume":
        window.open("/resume.pdf", "_blank", "noopener");
        return "opening resume.pdf…";
      case "echo":
        return arg;
      case "cat": {
        if (!arg) return "cat: missing file — try `ls`";
        if (arg === "resume.pdf") {
          window.open("/resume.pdf", "_blank", "noopener");
          return "resume.pdf is a binary file — opening it…";
        }
        return CAT[arg] ?? `cat: ${arg}: no such file — try \`ls\``;
      }
      case "sudo": {
        if (input.includes("hire")) {
          return (
            <>
              {"[sudo] password for recruiter: ********\n"}
              {"permission granted ✔\n"}
              {"ishak added to your team — run `contact` to finish setup."}
            </>
          );
        }
        return "recruiter is not in the sudoers file. this incident will be reported.";
      }
      case "cd":
      case "open": {
        const page = pages[arg.replace(/\.md$/, "").replace(/\/$/, "")];
        if (page) {
          navigate(page);
          return `opening ${arg}…`;
        }
        return `${cmd}: ${arg || "missing argument"}: not found — try \`ls\``;
      }
      case "exit":
        return "nice try. there is no escape — only `contact`.";
      default: {
        const page = pages[cmd.replace(/\.md$/, "")];
        if (page) {
          navigate(page);
          return `opening ${cmd}…`;
        }
        return `zsh: command not found: ${cmd} — try \`help\``;
      }
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const input = value;
      setValue("");
      if (input.trim()) {
        setHistory((h) => [...h, input]);
      }
      setHistoryIndex(-1);
      if (input.trim() === "clear") {
        setLines([]);
        return;
      }
      const output = run(input);
      setLines((prev) => [...prev, { id: nextId++, input, output }]);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (history.length === 0) return;
      const index = historyIndex === -1 ? history.length - 1 : Math.max(0, historyIndex - 1);
      setHistoryIndex(index);
      setValue(history[index]);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIndex === -1) return;
      const index = historyIndex + 1;
      if (index >= history.length) {
        setHistoryIndex(-1);
        setValue("");
      } else {
        setHistoryIndex(index);
        setValue(history[index]);
      }
    }
  };

  return (
    <div className="terminal-panel" onClick={() => inputRef.current?.focus()}>
      <div className="terminal-scroll" ref={scrollRef}>
        {lines.map((line) => (
          <div key={line.id}>
            {line.input !== undefined && (
              <p className="terminal-line">
                <span className="terminal-prompt">$ </span>
                {line.input}
              </p>
            )}
            {line.output !== undefined && (
              <p className="terminal-line terminal-output">{line.output}</p>
            )}
          </div>
        ))}
        <div className="terminal-input-row">
          <span className="terminal-prompt">$ </span>
          <input
            ref={inputRef}
            className="terminal-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            spellCheck={false}
            autoCapitalize="none"
            autoComplete="off"
            aria-label="Terminal input"
          />
        </div>
      </div>
    </div>
  );
};

export default Terminal;
